/* eslint-disable no-undef */

/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Feature, FeatureCollection } from '@turf/helpers';
import concat from 'lodash/concat';
import isNil from 'lodash/isNil';
import last from 'lodash/last';
import reject from 'lodash/reject';
import size from 'lodash/size';
import appTheme from '../components/common/theme/appTheme.json';
import FlightLegServiceAlerts, { ServiceAlert, Waypoint } from '../shared/entities/flightLegServiceAlerts';
import { MapType, Network } from '../shared/entities/beam';
const GEODESIC = false;

export enum ServiceAlertType {
  BEFORE_HAND_OFF = 'BEFORE_HAND_OFF',
  HAND_OFF = 'HAND_OFF',
  AFTER_HAND_OFF = 'AFTER_HAND_OFF',
  HNA = 'HNA',
  KA_HNA = 'KA_HNA',
  HNA_ZONE = 'HNA_ZONE',
  NO_COVERAGE = 'NO_COVERAGE',
  HCX_HNA = 'HCX_HNA',
}

export enum SegmentType {
  NOMINAL = 'NOMINAL',
  HNA = 'HNA',
  NO_COVERAGE = 'NO_COVERAGE',
}

interface ISegment {
  segmentType: SegmentType;
  latLngs: google.maps.LatLng[];
  altitudeRestriction: number;
}

class Segment implements ISegment {
  segmentType: SegmentType;
  latLngs: google.maps.LatLng[];
  altitudeRestriction: number;

  constructor(segmentType: SegmentType, latLngs: google.maps.LatLng[], altitudeRestriction: number) {
    this.segmentType = segmentType;
    this.latLngs = latLngs;
    this.altitudeRestriction = altitudeRestriction;
  }
}

// HNA Zones with an altitude of 99,999 ft are all airspace
export const HNA_ALL_ALTITUDE_RESTRICTION_ALTITUDE_FT = 99999;

// HNA Zones without altitude restriction are denoted with -1
export const HNA_NO_ALTITUDE_RESTRICTION = -1;

export const getSegmentColorDefinitions = () => {
  return {
    mptNominalServiceFlightPathColor: appTheme.mptNominalServiceFlightPathColor,
    mptHnaAllAltitudeFlightPathColor: appTheme.mptHnaAllAltitudeFlightPathColor,
    mptHnaAltitudeRestrictedFlightPathColor: appTheme.mptHnaAltitudeRestrictedFlightPathColor,
    mptNoCoverageFlightPathColor: appTheme.mptNoCoverageFlightPathColor,
    mptMarginalServiceColor: appTheme.mptMarginalServiceColor,
  };
};
export const getFlightPathSegments = (selectedFlightLegServiceAlerts: FlightLegServiceAlerts) => {
  let waypoints: Waypoint[] = selectedFlightLegServiceAlerts.waypoints.slice();
  let serviceAlerts: ServiceAlert[] = selectedFlightLegServiceAlerts.serviceAlerts
    .slice()
    .filter(
      (serviceAlert: ServiceAlert) =>
        serviceAlert.type !== ServiceAlertType.BEFORE_HAND_OFF && serviceAlert.type !== ServiceAlertType.AFTER_HAND_OFF,
    );
  let segments: ISegment[] = [];
  // if there are service alerts that are not handoffs, segment the flight path
  if (size(serviceAlerts)) {
    serviceAlerts.forEach((serviceAlert: ServiceAlert, index: number, serviceAlertsArray: ServiceAlert[]) => {
      if (!index) {
        const start: google.maps.LatLng = new google.maps.LatLng(
          selectedFlightLegServiceAlerts.waypoints[0].latitude,
          selectedFlightLegServiceAlerts.waypoints[0].longitude,
        );
        const middle = waypoints
          .filter((waypoint: Waypoint) => waypoint.timeSinceTakeoffMin <= serviceAlert.timeSinceTakeOffMin)
          .map((waypoint: Waypoint) => new google.maps.LatLng(waypoint.latitude, waypoint.longitude));
        const end: google.maps.LatLng = new google.maps.LatLng(serviceAlert.lat, serviceAlert.lon);
        const latLngs: google.maps.LatLng[] = concat(start, middle, end);
        segments.push(
          new Segment(
            getTakeoffSegmentType(selectedFlightLegServiceAlerts),
            latLngs,
            getAltitudeRestrictionForBeam(
              selectedFlightLegServiceAlerts.originBeamName,
              selectedFlightLegServiceAlerts.originBeamNetwork,
              selectedFlightLegServiceAlerts.originBeamMapType,
              selectedFlightLegServiceAlerts.beams,
            ),
          ),
        );
      } else {
        const start: google.maps.LatLng = new google.maps.LatLng(
          serviceAlertsArray[index - 1].lat,
          serviceAlertsArray[index - 1].lon,
        );
        const middle = waypoints
          .filter(
            (waypoint: Waypoint) =>
              waypoint.timeSinceTakeoffMin > serviceAlertsArray[index - 1].timeSinceTakeOffMin &&
              waypoint.timeSinceTakeoffMin < serviceAlert.timeSinceTakeOffMin,
          )
          .map((waypoint: Waypoint) => new google.maps.LatLng(waypoint.latitude, waypoint.longitude));
        const end: google.maps.LatLng = new google.maps.LatLng(serviceAlert.lat, serviceAlert.lon);
        const latLngs: google.maps.LatLng[] = concat(start, middle, end);
        segments.push(
          new Segment(
            getSegmentType(serviceAlertsArray[index - 1]),
            latLngs,
            getAltitudeRestrictionForBeam(
              serviceAlertsArray[index - 1].loginBeamName,
              serviceAlertsArray[index - 1].loginBeamNetwork,
              serviceAlertsArray[index - 1].loginBeamMapType,
              selectedFlightLegServiceAlerts.beams,
            ),
          ),
        );
      }
    });
    // add the landing
    const lastServiceAlert = last(serviceAlerts);
    const lastWaypoint = last(waypoints);
    if (!isNil(lastServiceAlert) && !isNil(lastWaypoint)) {
      const start: google.maps.LatLng = new google.maps.LatLng(lastServiceAlert.lat, lastServiceAlert.lon);
      const middle = waypoints
        .filter((waypoint: Waypoint) => waypoint.timeSinceTakeoffMin > lastServiceAlert.timeSinceTakeOffMin)
        .map((waypoint: Waypoint) => new google.maps.LatLng(waypoint.latitude, waypoint.longitude));
      const end: google.maps.LatLng = new google.maps.LatLng(lastWaypoint.latitude, lastWaypoint.longitude);
      segments.push(
        new Segment(
          getLandingSegmentType(selectedFlightLegServiceAlerts),
          concat(start, middle, end),
          getAltitudeRestrictionForBeam(
            selectedFlightLegServiceAlerts.destinationBeamName,
            selectedFlightLegServiceAlerts.destinationBeamNetwork,
            selectedFlightLegServiceAlerts.destinationBeamMapType,
            selectedFlightLegServiceAlerts.beams,
          ),
        ),
      );
    }
  } else {
    // Else there are no service alerts or no service alerts that are not handoffs, color the segment based on the take off since
    // the flight is going to take off and land without changing hna's or changing from coverage to no coverage. So if its taking off out
    // of coverage and there are no service alerts then its gonna stay out of coverage
    segments = [
      new Segment(
        getTakeoffSegmentType(selectedFlightLegServiceAlerts),
        waypoints.map((waypoint: Waypoint) => new google.maps.LatLng(waypoint.latitude, waypoint.longitude)),
        getAltitudeRestrictionForBeam(
          selectedFlightLegServiceAlerts.originBeamName,
          selectedFlightLegServiceAlerts.originBeamNetwork,
          selectedFlightLegServiceAlerts.originBeamMapType,
          selectedFlightLegServiceAlerts.beams,
        ),
      ),
    ];
  }

  return (
    segments
      // Remove *CONSECUTIVE* duplicate latLngs from each segment. Don't remove all duplicates as a duplicate latLng later in the list (non adjacent)
      // could happen if the terminal were to circle back and cut across it's flight path (probably unlikely to get the exactly latLng but theoretically
      // possible)
      //
      // note:  For segments with only 2 latLngs that could be duplicate, keep them, don't process and remove duplicates, so an array of 1 item isn't
      // given to the google maps Polyline constructor
      .map(
        (segment: ISegment) =>
          ({
            ...segment,
            latLngs:
              size(segment.latLngs) > 2
                ? reject(
                    segment.latLngs,
                    (latLng: google.maps.LatLng, index: number) =>
                      index &&
                      segment.latLngs[index - 1].lat() === latLng.lat() &&
                      segment.latLngs[index - 1].lng() === latLng.lng(),
                  )
                : segment.latLngs,
          } as ISegment),
      )
      // Get a list of google maps Polylines from each segment (stroke color, path)
      .map(
        (segment: ISegment) =>
          ({
            ...segment,
            latLngs: reject(
              segment.latLngs,
              (latLng: google.maps.LatLng, index: number) =>
                index &&
                segment.latLngs[index - 1].lat() === latLng.lat() &&
                segment.latLngs[index - 1].lng() === latLng.lng(),
            ),
          } as ISegment),
      )
      .map((segment: ISegment) => getSegmentPolyline(segment, getSegmentColorDefinitions()))
  );
};

export const getAltitudeRestrictionForBeam = (
  beamName: string | undefined,
  networkType: string | undefined,
  mapType: string | undefined,
  beams: FeatureCollection,
): number => {
  let altitudeRestriction = HNA_NO_ALTITUDE_RESTRICTION;
  const feature: Feature | undefined = beams?.features?.find(
    (feature: Feature) => feature.properties?.name === beamName &&
                          feature.properties?.network === networkType &&
                          feature.properties?.mapType === mapType,
  );
  if (!isNil(feature)) {
    altitudeRestriction = feature.properties?.hnaAltitude;
  }
  return altitudeRestriction;
};

const getSegmentPolyline = (
  segment: ISegment,
  segmentColorDefinitions: { [key: string]: string },
): google.maps.Polyline => {
  const segmentColor = getSegmentColorBySegmentType(
    segment.segmentType,
    segment.altitudeRestriction,
    segmentColorDefinitions,
  );
  const polylineOptions = {
    path: segment.latLngs,
    geodesic: GEODESIC, // Don't render the path as geodesic to represent the closest point between two points on a sphere
    strokeColor: segmentColor,
    strokeOpacity: 1,
    strokeWeight: 4,
    zIndex: 1,
    clickable: false,
  };

  return new google.maps.Polyline(polylineOptions);
};
export const getSegmentType = (serviceAlert: ServiceAlert) => {
  let type = SegmentType.NOMINAL;
  if (
    ((serviceAlert.type === ServiceAlertType.HNA || serviceAlert.type === ServiceAlertType.HNA_ZONE) &&
      serviceAlert.loginBeamType === ServiceAlertType.HNA) ||
    ((serviceAlert.type === ServiceAlertType.KA_HNA || serviceAlert.type === ServiceAlertType.HNA_ZONE) &&
      serviceAlert.loginBeamType === ServiceAlertType.KA_HNA) ||
    ((serviceAlert.type === ServiceAlertType.HCX_HNA || serviceAlert.type === ServiceAlertType.HNA_ZONE) &&
      serviceAlert.loginBeamType === ServiceAlertType.HCX_HNA) ||
    // You are flying back into coverage but into an HNA Zone:
    (serviceAlert.type === ServiceAlertType.NO_COVERAGE &&
      serviceAlert.loginBeamInCoverage &&
      (serviceAlert.loginBeamType === ServiceAlertType.HNA ||
        serviceAlert.loginBeamType === ServiceAlertType.KA_HNA ||
        serviceAlert.loginBeamType === ServiceAlertType.HCX_HNA))
  ) {
    type = SegmentType.HNA;
  } else if (serviceAlert.type === ServiceAlertType.NO_COVERAGE && !serviceAlert.loginBeamInCoverage) {
    type = SegmentType.NO_COVERAGE;
  }
  return type;
};

export const getTakeoffSegmentType = (selectedFlightLegServiceAlerts: FlightLegServiceAlerts) => {
  if (!selectedFlightLegServiceAlerts.originInCoverage) {
    return SegmentType.NO_COVERAGE;
  } else if (
    selectedFlightLegServiceAlerts.originBeamType === ServiceAlertType.HNA ||
    selectedFlightLegServiceAlerts.originBeamType === ServiceAlertType.KA_HNA ||
    selectedFlightLegServiceAlerts.originBeamType === ServiceAlertType.HCX_HNA ||
    selectedFlightLegServiceAlerts.originBeamType === ServiceAlertType.HNA_ZONE
  ) {
    return SegmentType.HNA;
  } else {
    return SegmentType.NOMINAL;
  }
};

export const getLandingSegmentType = (selectedFlightLegServiceAlerts: FlightLegServiceAlerts) => {
  if (!selectedFlightLegServiceAlerts.destinationInCoverage) {
    return SegmentType.NO_COVERAGE;
  } else if (
    selectedFlightLegServiceAlerts.destinationBeamType === ServiceAlertType.HNA ||
    selectedFlightLegServiceAlerts.destinationBeamType === ServiceAlertType.KA_HNA ||
    selectedFlightLegServiceAlerts.destinationBeamType === ServiceAlertType.HCX_HNA ||
    selectedFlightLegServiceAlerts.destinationBeamType === ServiceAlertType.HNA_ZONE
  ) {
    return SegmentType.HNA;
  } else {
    return SegmentType.NOMINAL;
  }
};

export const getSegmentColorBySegmentType = (
  segmentType: SegmentType,
  altitudeRestriction: number,
  segmentColorDefinitions: { [key: string]: string },
) => {
  let segmentColor: string = segmentColorDefinitions.mptNominalServiceFlightPathColor;
  switch (segmentType) {
    case SegmentType.HNA:
      segmentColor =
        altitudeRestriction === HNA_ALL_ALTITUDE_RESTRICTION_ALTITUDE_FT
          ? segmentColorDefinitions.mptHnaAllAltitudeFlightPathColor
          : segmentColorDefinitions.mptHnaAltitudeRestrictedFlightPathColor;
      break;
    case SegmentType.NO_COVERAGE:
      segmentColor = segmentColorDefinitions.mptNoCoverageFlightPathColor;
      break;
    default:
      break;
  }
  return segmentColor;
};
